const CORS_PROXIES=[
  {name: 'allorigins-get',build: (url)=> `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`,extract: (data)=> data.contents},
  {name: 'allorigins-raw',build: (url)=> `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,extract: (data)=> (typeof data==='string' ? data : null),isRaw: true},
  {name: 'corsproxy-io',build: (url)=> `https://corsproxy.io/?${encodeURIComponent(url)}`,extract: (data)=> (typeof data==='string' ? data : null),isRaw: true},
  {name: 'codetabs',build: (url)=> `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(url)}`,extract: (data)=> (typeof data==='string' ? data : null),isRaw: true},
];

const NETLIFY_CRAWL_FUNCTION='/.netlify/functions/source-crawl';

const tryProxy=async (proxy,url,signal)=> {
  const proxyUrl=proxy.build(url);
  const res=await fetch(proxyUrl,{
    signal,
    headers: {'Accept': 'text/html,application/json,*/*'},
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  let html;
  if (proxy.isRaw) {
    html=await res.text();
  } else {
    const data=await res.json();
    html=proxy.extract(data);
  }
  if (!html || typeof html !=='string' || html.length < 200) {
    throw new Error('Response too short or empty');
  }
  if (
    html.includes('Access Denied') ||
    html.includes('403 Forbidden') ||
    html.includes('Blocked by') ||
    html.includes('cf-error') ||
    html.includes('Enable JavaScript and cookies')
  ) {
    throw new Error('Site returned access-denied page');
  }
  return html;
};

// Public CORS proxies are unreliable, so race them in parallel instead of
// waiting for each one to time out in turn. The first usable page wins and
// the rest are cancelled.
const fetchViaProxies=async (url)=> {
  const controller=new AbortController();
  const timeout=AbortSignal.timeout(15000);
  const signal=AbortSignal.any ? AbortSignal.any([controller.signal,timeout]) : timeout;
  const errors=[];
  try {
    return await Promise.any(CORS_PROXIES.map(async (proxy)=> {
      try {
        const html=await tryProxy(proxy,url,signal);
        return {html,proxyName: proxy.name};
      } catch (err) {
        errors.push(`${proxy.name}: ${err.message}`);
        throw err;
      }
    }));
  } catch {
    throw new Error(`All public proxies failed:\n ${errors.join('\n ')}`);
  } finally {
    controller.abort();
  }
};

const tryNetlifyFunction=async (url)=> {
  const response=await fetch(NETLIFY_CRAWL_FUNCTION,{
    method: 'POST',
    headers: {'content-type': 'application/json'},
    body: JSON.stringify({url}),
    signal: AbortSignal.timeout(15000),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data=await response.json();
  if (data.error) throw new Error(data.error);
  return {
    text: data.text,
    wordCount: data.wordCount,
    proxyUsed: 'netlify-function',
    error: null,
  };
};

const extractTextFromHtml=(html)=> {
  let text=html
    .replace(/<script[\s\S]*?<\/script>/gi,' ')
    .replace(/<style[\s\S]*?<\/style>/gi,' ')
    .replace(/<nav[\s\S]*?<\/nav>/gi,' ')
    .replace(/<footer[\s\S]*?<\/footer>/gi,' ')
    .replace(/<header[\s\S]*?<\/header>/gi,' ')
    .replace(/<!--[\s\S]*?-->/g,' ');

  text=text
    .replace(/<\/?(p|div|h[1-6]|li|br|tr|td|th|section|article|blockquote)[^>]*>/gi,'\n')
    .replace(/<[^>]+>/g,' ');

  text=text
    .replace(/&amp;/g,'&')
    .replace(/&lt;/g,'<')
    .replace(/&gt;/g,'>')
    .replace(/&quot;/g,'"')
    .replace(/&#39;/g,"'")
    .replace(/&nbsp;/g,' ');

  text=text
    .replace(/[ \t]+/g,' ')
    .replace(/\n{3,}/g,'\n\n')
    .trim();

  return text;
};

const truncateText=(text,maxChars=12000)=> {
  if (text.length <=maxChars) return text;
  return text.slice(0,maxChars) + '\n\n[... content truncated for token limit ...]';
};

export const crawlPage=async (url)=> {
  let functionFailure='';
  try {
    return await tryNetlifyFunction(url);
  } catch (functionError) {
    functionFailure=/404|JSON|Unexpected token/i.test(functionError.message)
      ? 'netlify-function: not deployed (Netlify drag-and-drop deploys do not include functions; deploy with the Netlify CLI or Git)'
      : `netlify-function: ${functionError.message}`;
  }

  try {
    const {html,proxyName}=await fetchViaProxies(url);
    const raw=extractTextFromHtml(html);
    const text=truncateText(raw);
    const wordCount=text.split(/\s+/).filter(Boolean).length;
    return {text,wordCount,proxyUsed: proxyName,error: null};
  } catch (err) {
    return {text: null,wordCount: 0,proxyUsed: null,error: `${functionFailure}\n ${err.message}`};
  }
};